import React, { useState } from "react";
import { Download } from "lucide-react";
import { toast } from "react-toastify";
import { useAppSelector } from "../../../store/hooks";
import {
  hasPermission,
  FEATURES,
  PERMISSIONS,
  getUserRole,
} from "../../../config/roleFeatures";
import StatusBadge from "./StatusBadge";
import ArrowDownUpIcon from "../../../assets/icons/ArrowDownUpIcon";
import EyeIcon from "../../../assets/icons/EyeIcon";
import RedDeleteIcon from "../../../assets/icons/RedDeleteIcon";
import PrintIcon from "../../../assets/icons/PrintIcon";
import { useSelector } from "react-redux";
import DeleteIcon from "../../../assets/delete-modal-icon.svg";
import { salesAPI } from "../../../services";

const SalesTable = ({
  sales,
  loading,
  pagination,
  onPageChange,
  onViewInvoice,
  onPrintInvoice,
  onDeleteSale,
  onUpdateStatus,
  showProfits,
  showTotalSale,
}) => {
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const { user } = useAppSelector((state) => state.auth);
  const userRole = getUserRole(user);
  const canDelete = hasPermission(userRole, FEATURES.SALES, PERMISSIONS.DELETE);
  const [sortConfig, setSortConfig] = useState({
    amount: { direction: null },
    profit: { direction: null },
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingSale, setDeletingSale] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [exportingSaleId, setExportingSaleId] = useState(null);

  const handleExportSale = async (sale) => {
    if (sale.status?.toLowerCase() !== "publish") {
      toast.warning("Only published sales can be exported for retail stock import.");
      return;
    }
    setExportingSaleId(sale.id);
    try {
      const filename = await salesAPI.exportSale(sale.id, "xlsx");
      toast.success(`Exported ${filename}`);
    } catch (error) {
      toast.error(error?.message || "Failed to export sale.");
    } finally {
      setExportingSaleId(null);
    }
  };

  const handleDeleteClick = (sale) => {
    setDeletingSale(sale);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingSale) return;

    setIsDeleting(true);
    try {
      await onDeleteSale(deletingSale.id);
      setShowDeleteModal(false);
      setDeletingSale(null);
    } catch (error) {
      console.error("Error deleting sale:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingSale(null);
  };

  const handlePrevious = () => {
    if (pagination.previous) {
      onPageChange(pagination.currentPage - 1);
    }
  };

  const handleNext = () => {
    if (pagination.next) {
      onPageChange(pagination.currentPage + 1);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const currentDirection = prev[key].direction;
      let newDirection = "asc";

      // 3-state cycle: null -> asc -> desc -> null
      if (currentDirection === null) {
        newDirection = "asc";
      } else if (currentDirection === "asc") {
        newDirection = "desc";
      } else if (currentDirection === "desc") {
        newDirection = null; // Reset to no sorting
      }

      // Create new config with only the clicked column active
      const newConfig = {
        amount: { direction: null },
        profit: { direction: null },
      };

      // Only set the clicked column's direction
      newConfig[key] = { direction: newDirection };

      return newConfig;
    });
  };

  const getSortedSales = () => {
    let sortedSales = [...sales];

    // Apply sorting - only one column can be sorted at a time
    if (sortConfig.amount.direction) {
      sortedSales.sort((a, b) => {
        const aAmount = parseFloat(a.amount) || 0;
        const bAmount = parseFloat(b.amount) || 0;

        if (aAmount < bAmount) {
          return sortConfig.amount.direction === "asc" ? -1 : 1;
        }
        if (aAmount > bAmount) {
          return sortConfig.amount.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    } else if (sortConfig.profit.direction) {
      sortedSales.sort((a, b) => {
        const aProfit = parseFloat(a.profit) || 0;
        const bProfit = parseFloat(b.profit) || 0;

        if (aProfit < bProfit) {
          return sortConfig.profit.direction === "asc" ? -1 : 1;
        }
        if (aProfit > bProfit) {
          return sortConfig.profit.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedSales;
  };
  if (loading) {
    return <div className="sales-loading">Loading sales data...</div>;
  }

  if (!sales.length) {
    return (
      <div className="sales-empty">
        <p>No sales found matching your criteria</p>
      </div>
    );
  }

  return (
    <>
      <div className="sales-table-responsive">
        <table className="sales-table">
          <thead>
            <tr>
              <th>INVOICE ID</th>
              <th>CUSTOMER</th>
              <th>DATE & TIME</th>
              <th>
                <button
                  className={isPartsURL ? "sales-sort-header-parts" : isAccessoriesURL ? "sales-sort-header-acc" : "sales-sort-header"}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSort("amount");
                  }}
                >
                  AMOUNT{" "}
                  <ArrowDownUpIcon
                    color={sortConfig.amount.direction ? isPartsURL ? "#DB2E31" : isAccessoriesURL ? "#72BF48" : "#2F80ED" : "#57606A"}
                  />
                </button>
              </th>
              <th>
                <button
                  className={isPartsURL ? "sales-sort-header-parts" : isAccessoriesURL ? "sales-sort-header-acc" : "sales-sort-header"}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSort("profit");
                  }}
                >
                  PROFIT{" "}
                  <ArrowDownUpIcon
                    color={sortConfig.profit.direction ? isPartsURL ? "#DB2E31" : isAccessoriesURL ? "#72BF48" : "#2F80ED" : "#57606A"}
                  />
                </button>
              </th>
              <th>CREATOR</th>
              <th>STATUS</th>
              <th style={{ textAlign: "center" }}>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {getSortedSales().map((sale, index) => (
              <tr
                key={`${sale.id}-${sortConfig.amount.direction}-${sortConfig.profit.direction}-${index}`}
                className={
                  sale.canPublish === false ? "sales-row-cannot-publish" : ""
                }
              >
                <td className="sales-invoice-id">{sale.invoiceId}</td>
                <td className="sales-customer-name">{sale.customerName}</td>
                <td className="sales-date-time">{sale.dateTime}</td>
                <td className="sales-amount">€{sale.amount}</td>
                <td
                  className={`sales-profit-stars ${
                    parseFloat(sale.profit) < 0 ? "negative" : ""
                  }`}
                >
                  {showProfits ? `$${sale.profit}` : "*******"}
                </td>
                <td className="sales-creator">{sale.creator}</td>

                {/* Status */}
                <td>
                  <StatusBadge status={sale.status} />
                </td>

                {/* Action Buttons */}
                <td>
                  <div className="action-buttons">
                    <button
                      className={`${
                        isPartsURL
                          ? "action-btn-parts view"
                          : isAccessoriesURL
                          ? "action-btn-acc view"
                          : "action-btn view"
                      }`}
                      onClick={() => onViewInvoice(sale)}
                      title="View Invoice"
                    >
                      <EyeIcon
                        fill={
                          isPartsURL
                            ? "var(--primary-color-parts)"
                            : isAccessoriesURL
                            ? "var(--primary-color-acc)"
                            : "var(--primary-color-mobile)"
                        }
                      />
                    </button>

                    <button
                      className={`${
                        isPartsURL
                          ? "action-btn-parts view"
                          : isAccessoriesURL
                          ? "action-btn-acc view"
                          : "action-btn view"
                      } ${
                        sale.status.toLowerCase() === "cancelled"
                          ? "disabled"
                          : ""
                      }`}
                      onClick={() => {
                        if (sale.status.toLowerCase() !== "cancelled") {
                          onPrintInvoice(sale);
                        }
                      }}
                      disabled={sale.status.toLowerCase() === "cancelled"}
                      title={
                        sale.status.toLowerCase() === "cancelled"
                          ? "Print not available for this status"
                          : "Print Invoice"
                      }
                    >
                      <PrintIcon
                        fill={
                          sale.status.toLowerCase() === "cancelled"
                            ? "#AFB8C1"
                            : isPartsURL
                            ? "var(--primary-color-parts)"
                            : isAccessoriesURL
                            ? "var(--primary-color-acc)"
                            : "var(--primary-color-mobile)"
                        }
                      />
                    </button>

                    {sale.status.toLowerCase() === "publish" && (
                      <button
                        className={`${
                          isPartsURL
                            ? "action-btn-parts view"
                            : isAccessoriesURL
                            ? "action-btn-acc view"
                            : "action-btn view"
                        }`}
                        onClick={() => handleExportSale(sale)}
                        disabled={exportingSaleId === sale.id}
                        title="Export Excel for Electronic POS stock import"
                      >
                        <Download
                          size={16}
                          color={
                            isPartsURL
                              ? "var(--primary-color-parts)"
                              : isAccessoriesURL
                              ? "var(--primary-color-acc)"
                              : "var(--primary-color-mobile)"
                          }
                        />
                      </button>
                    )}

                    {canDelete && (
                      <button
                        className={`action-btn delete ${
                          sale.status.toLowerCase() === "cancelled" ||
                          sale.status.toLowerCase() === "publish"
                            ? "disabled"
                            : ""
                        }`}
                        onClick={() => {
                          if (
                            sale.status.toLowerCase() !== "cancelled" &&
                            sale.status.toLowerCase() !== "publish"
                          ) {
                            handleDeleteClick(sale);
                          }
                        }}
                        disabled={
                          sale.status.toLowerCase() === "cancelled" ||
                          sale.status.toLowerCase() === "publish"
                        }
                        title={
                          sale.status.toLowerCase() === "cancelled" ||
                          sale.status.toLowerCase() === "publish"
                            ? "Delete not available for this status"
                            : "Delete Sale"
                        }
                      >
                        <RedDeleteIcon
                          disabled={
                            sale.status.toLowerCase() === "cancelled" ||
                            sale.status.toLowerCase() === "publish"
                          }
                        />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {pagination.count > 25 && (
        <div className="sales-pagination-container">
          <div className="sales-pagination-info">
            Page {pagination.currentPage} of {pagination.totalPages}
          </div>
          <div className="sales-pagination-buttons">
            <button
              className={isPartsURL ? "sales-pagination-btn-parts" : isAccessoriesURL ? "sales-pagination-btn-acc" : "sales-pagination-btn"}
              onClick={handlePrevious}
              disabled={!pagination.previous}
            >
              Previous
            </button>
            <button
              className={isPartsURL ? "sales-pagination-btn-parts" : isAccessoriesURL ? "sales-pagination-btn-acc" : "sales-pagination-btn"}
              onClick={handleNext}
              disabled={!pagination.next}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && deletingSale && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <div className="modal-icon">
              <img src={DeleteIcon} alt="Delete" />
            </div>
            <h3 className="modal-title">Delete Sale</h3>
            <p className="modal-message">
              Are you sure you want to delete sale{" "}
              <b>{deletingSale.invoiceId}</b>?
              <br />
              This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                className="modal-cancel-btn"
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="modal-delete-btn"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SalesTable;
